import { isFlatStyle, type PieceStyle } from '@draughts/shared';
import { relativeLuminance } from '../../utils/color-oklch';
import { darken, type ColorPair } from './piece-themes';

/**
 * I PALLINI DELLA DAMA BLOCCATA — geometria e inchiostro.
 *
 * Nella Frisone e nella Frysk! una dama non puo' muovere piu' di tre volte di fila senza
 * catturare. La damiera lo dice con un pallino al piede della dama per ogni mossa gia'
 * fatta: tre pallini, dama ferma. Le CONDIZIONI non stanno qui ma in
 * `getBlockedKingState` del motore, che le dice anche al filtro delle mosse legali.
 *
 * Qui c'e' solo il come si disegnano. Sta in un file suo, e non dentro `GameBoard`, per la
 * stessa ragione di `board-highlight-colors.ts`: la leggibilita' di un segno e' una
 * proprieta' MISURABILE, e la misura va in un test che non deve montare una scacchiera.
 *
 * DOVE NON COMPAIONO. Nel replay dallo storico: `GameReplay` ha un `applyMove` locale suo
 * che non tiene lo stato frisone, quindi la' la striscia non esiste. Non e' una
 * dimenticanza, e' un lavoro a parte.
 */

/**
 * Il raggio del pallino, in frazione del lato di casella: 6.3 unita' su casella da 90,
 * cioe' 5.0px su una damiera larga 360 (10x10, la sola misura che Frisone e Frysk!
 * conoscono). Meta' del pallino delle mosse legali, ed e' giusto: quello e' un invito,
 * questo e' uno stato.
 */
export const BK_DOT_R = 0.07;
/** Il passo fra i centri: 16.2 unita', cioe' 3.6 di casella nuda fra due pallini. */
export const BK_DOT_STEP = 0.18;
/**
 * Quanto sotto il centro della casella corre la fila.
 *
 * ANCORATA ALLA CASELLA, NON AL PEZZO, ed e' una scelta non un ripiego. Il fondo della
 * sagoma (`baseY` in Piece.tsx) dipende da stile x forma x tipo di corona — fra la dama
 * 3D e la piatta ballano tre unita', e la stella scende piu' del tondo — quindi
 * inseguirlo da qui vorrebbe dire ricopiare mezzo `Piece.tsx` e lasciarlo derivare.
 *
 * Il prezzo e' che la fila SORMONTA il piede del pezzo, di piu' o di meno secondo lo
 * stile. Non c'e' alternativa: fra il fondo della dama 3D e il bordo della casella
 * corrono 6.1 unita' su 90, cioe' 2.4px su una damiera da 360, e un pallino largo 2.4px
 * con un contorno sub-pixel non si vede. La sovrapposizione e' l'effetto lidraughts.
 *
 * `BK_DOT_CY + BK_DOT_R + contorno/2 < 0.5`: ogni pallino resta dentro la PROPRIA
 * casella, non sbrodola in quella sotto. Lo verifica un test.
 */
export const BK_DOT_CY = 0.415;
/** Lo spessore del contorno, in unita' SVG: scala con la damiera come tutto il resto. */
export const BK_DOT_RIM_W = 1.6;

/** Oltre questa luminanza della tinta sotto il pallino, il nucleo va scuro. */
const CORE_DARK_ABOVE = 0.18;

/** Nucleo scuro con alone chiaro: per i pezzi chiari. */
const INK_DARK = { core: '#141414', rim: 'rgba(250,250,250,0.9)' } as const;
/** Nucleo chiaro con alone scuro: per i pezzi scuri. */
const INK_LIGHT = { core: '#FAFAFA', rim: 'rgba(5,5,5,0.78)' } as const;

/**
 * L'ALONE DEI PALLINI D'ALLARME: il polo del NUCLEO neutro, non quello del suo alone.
 *
 * Misurato, e il risultato ribalta due ipotesi di fila. L'ambra `--color-warning`
 * (#F39C12) sta a meta' scala, quindi contro un fondo di mezzo tono non si stacca:
 * contro la parete di un pezzo chiaro fa 1.6:1, contro tre delle caselle giocabili
 * spedite 1.2-1.6:1. L'ambra NON puo' essere cio' che rende visibile il pallino — lo
 * rende solo riconoscibile. La leggibilita' deve passare all'alone.
 *
 * E allora l'alone non puo' essere fisso. Un alone sempre scuro — la prima ipotesi —
 * regge sui fondi chiari (5.4:1) ma crolla su quelli scuri, dove misura 3.05:1 nel caso
 * peggiore della spazzata: il nucleo ambra e l'alone scuro sono allora entrambi vicini al
 * fondo. L'alone deve prendersi il lavoro che il nucleo non puo' fare, cioe' portare il
 * POLO CHE IL NUCLEO AVREBBE USATO: scuro sui fondi chiari, chiaro su quelli scuri. Cosi'
 * un pallino d'allarme misura quanto i neutri (4.2:1 nel peggiore dei casi) invece di
 * scendere al pavimento dei 3:1.
 *
 * Ne segue che la fila ferma ha l'alone ROVESCIATO rispetto a una fila ancora in corso:
 * l'ambra la prendono tutti e tre i pallini insieme (l'accento su uno solo cadeva a destra
 * di una fila centrata — vedi GameBoard.tsx), quindi la differenza visibile non sta fra
 * pallini della stessa fila ma fra fila neutra, a una o due mosse, e fila ambra. E'
 * benvenuta: quella fila deve distinguersi.
 *
 * Scartato `--color-warning-text` (#A35F00), che pure e' il token «per fondi chiari»:
 * misura 1.0-1.9:1 su quattro caselle giocabili, cioe' PEGGIO del colore da superficie.
 */
const RIM_DARK = 'rgba(5,5,5,0.78)';
const RIM_LIGHT = 'rgba(250,250,250,0.9)';

export interface BlockedKingDotInk {
  /** Il riempimento dei pallini neutri. */
  core: string;
  /** Il contorno dei pallini neutri: di chiarezza opposta al nucleo. */
  rim: string;
  /** Il contorno dei pallini quando la dama e' ferma, che hanno l'ambra come riempimento. */
  blockingRim: string;
}

/**
 * L'inchiostro dei pallini per un pezzo: un NUCLEO e un ALONE di chiarezza opposta.
 *
 * E' l'impianto di `.board-coord` (le etichette di casella), che con riempimento e
 * contorno opposti e `paint-order: stroke` regge sopra la venatura della lastra e sopra
 * gli aloni dell'ultima mossa. Due strati opposti si leggono su un fondo qualunque per
 * costruzione, e qui serve proprio quello: il pallino sta a cavallo fra il piede del
 * pezzo e la casella, quindi il fondo e' DUE fondi, non uno.
 *
 * Per la stessa ragione NON si deriva dalla casella come gli altri segni (`--hl-dot`):
 * quella scala e' calibrata contro il colore della casella misurando il composito a
 * meta' opacita', e non descrive un segno opaco che per metà cade sul pezzo. E `--hl-dot`
 * e' il verde della DESTINAZIONE: usarlo per una restrizione farebbe leggere «puoi andare
 * li'» dove si dice «questa dama ha quasi finito».
 *
 * LA TINTA DI RIFERIMENTO E' LA PARETE, NON LA FACCIA, e questa e' la correzione che la
 * misura ha imposto: il pallino non cade sull'ultimo stop del gradiente della faccia ma
 * sul fianco estruso del pezzo, che `reliefShades` deriva come `darken(edge, 0.06..0.42)`.
 * Letta sulla faccia, la soglia sbaglia la polarita' su un pezzo personalizzato di mezzo
 * tono — edge 0.25, parete 0.11 — e il nucleo scelto misurerebbe 1.9:1. Sugli stili
 * piatti la parete non esiste e la faccia e' la tinta giusta.
 */
export function blockedKingDotInk(
  pair: ColorPair,
  style: PieceStyle,
  isWhite: boolean,
): BlockedKingDotInk {
  const stops = isWhite ? pair.white : pair.dark;
  const edge = stops[stops.length - 1]?.color ?? '#888888';
  const tone = isFlatStyle(style) ? edge : darken(edge, 0.3);
  const coreGoesDark = relativeLuminance(tone) > CORE_DARK_ABOVE;
  const ink = coreGoesDark ? INK_DARK : INK_LIGHT;
  return { core: ink.core, rim: ink.rim, blockingRim: coreGoesDark ? RIM_DARK : RIM_LIGHT };
}
